import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Alert, Platform, Text, View, StyleSheet, Linking } from 'react-native';

import { SCREENS } from '~constants';
import { appScreens } from '../screens';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import useTabBar from './zustand';
import { useTranslation } from 'react-i18next';
import { useGetUserAccount } from '~hooks/user/useGetUser';
import { useUserStore } from '~providers/userStore/zustand';
import { useNavigation } from '@react-navigation/native';
import { NavigationProps } from '~types/navigation/navigation.type';
// Conditionally import OneSignal for native platforms only
import * as oneSignalStub from '~services/web/oneSignalStub';
let OneSignal: any = null;
if (Platform.OS !== 'web') {
  OneSignal = require('react-native-onesignal').default;
} else {
  OneSignal = oneSignalStub.OneSignal;
}
import { registerListenerWithFCM } from '~Utils/fcmHelper';
import useDeepLinking from './useDeepLinking';
import firestore from '@react-native-firebase/firestore';
import { ChatType, ChatTypeWithKey } from '~types/chat';
import { useTheme } from '~contexts/ThemeContext';
import { useUnreadNotificationsCount } from '~hooks/notifications/useUnreadNotificationsCount';
import { useNotificationContext } from '~contexts/NotificationContext';

const Tabs = createBottomTabNavigator();

const AppScreens = () => {
  const { colors } = useTheme();
  const { isTabBarDisabled } = useTabBar();
  const user = useUserStore(state => state.user);
  const uid = user?.uid;
  const { t } = useTranslation();
  useDeepLinking();
  const { refetch } = useGetUserAccount(user?.uid!);
  const { navigate } = useNavigation<NavigationProps>();
  const [unreadChatCount, setUnReadChatCount] = useState<number | null>(null);
  const isMountedRef = useRef(true);
  
  // Get unread notifications count for badge
  const { data: unreadNotificationsCount, isError: isNotificationCountError } = useUnreadNotificationsCount();
  const { setUnreadCount, optimisticUnreadCount } = useNotificationContext();

  // Sync the context with the actual data when it changes
  useEffect(() => {
    if (unreadNotificationsCount !== undefined && !isNotificationCountError) {
      setUnreadCount(unreadNotificationsCount);
    }
  }, [unreadNotificationsCount, setUnreadCount, isNotificationCountError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (uid) {
      const unsubscribe = firestore()
        .collection('chats')
        .where('userIds', 'array-contains', uid)
        .onSnapshot(querySnapshot => {
          // Check if component is still mounted before updating state
          if (!isMountedRef.current) {
            return;
          }

          const conversations: ChatTypeWithKey[] = [];
          let unReadCount = 0;
          querySnapshot.forEach(documentSnapshot => {
            const data = documentSnapshot.data() as ChatType; // Cast Firestore data to our ChatData type
            conversations.push({
              ...data,
              key: documentSnapshot.id,
            });
            const unread = data?.history.filter(
              message => message?.readUserIds && !message.readUserIds.includes(uid),
            ).length;
            unReadCount = unReadCount + unread;
          });
          setUnReadChatCount(unReadCount);
        });

      // Clean up subscription on unmount
      return () => unsubscribe();
    }
  }, [uid]);

  useEffect(() => {
    const unsubscribe = registerListenerWithFCM();
    return unsubscribe;
  }, []);

  const openEvent = useCallback(
    async (eventID: string) => {
      const data = await refetch();
      console.log(eventID);

      if (data?.data) {
        navigate(SCREENS.HOME_STACK, { eventId: Number(eventID) });
        return;
      }
      Alert.alert('No permission to event');
    },
    [navigate, refetch],
  );

  useEffect(() => {
    if (Platform.OS === 'web') return; // Deep linking not supported on web

    const handleDeepLink = ({ url }: { url: string }) => {
      console.log(url);
      const parts = url.split('/').filter(item => item);
      console.log(parts);
      const eventID = parts[parts.length - 1];
      console.log(eventID);
      openEvent(eventID);
    };
    // Check for the initial deep link when the app is launched
    const checkInitialURL = async () => {
      const initialUrl = await Linking.getInitialURL();
      if (initialUrl) {
        console.log(`Initial deep link URL: ${initialUrl}`);
        handleDeepLink({ url: initialUrl });
      }
    };

    // Add an event listener for deep linking when the app is already running
    const linkingListener = Linking.addListener('url', handleDeepLink);

    checkInitialURL();

    return () => {
      // Clean up the event listeners
      linkingListener.remove();
    };
  }, [openEvent]);

  const openChat = useCallback(
    async ({ chatId, chatType }: { chatId: string; chatType: string }) => {
      const data = await refetch();
      if (data?.data) {
        navigate(SCREENS.CHAT_STACK, { key: chatId, chatType });
        return;
      }
      Alert.alert('No permission to chat');
    },
    [navigate, refetch],
  );

  useEffect(() => {
    if (Platform.OS === 'web') return; // OneSignal not available on web

    OneSignal.setNotificationOpenedHandler((notification: any) => {
      const additionalData = notification.notification.additionalData as {
        type?: string;
        chat_id?: string;
        event_id?: string;
        order_id?: string;
        refineSubcategories?: boolean;
      };
      console.log(additionalData, 'additionalDataadditionalData');
      if (additionalData?.chat_id || additionalData?.type === 'AdminChat') {
        openChat({
          chatId: additionalData?.chat_id || '',
          chatType: additionalData?.type || '',
        });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_UPDATED') {
        navigate(SCREENS.HOME_EVENT, { eventId: Number(additionalData.event_id) });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_COMMENT') {
        navigate(SCREENS.HOME_EVENT, { eventId: Number(additionalData.event_id) });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_CANCELLED') {
        navigate(SCREENS.HOME_EVENT, { eventId: Number(additionalData.event_id) });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_REQUEST_UPDATED') {
        navigate(SCREENS.HOME_EVENT, { eventId: Number(additionalData.event_id) });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_REMINDER') {
        navigate(SCREENS.HOME_EVENT, { eventId: Number(additionalData.event_id) });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_REVIEW_REMINDER') {
        const data = {
          event_id: additionalData.event_id,
        };
        navigate(SCREENS.RATE_EVENT, { event: data });
      } else if (additionalData?.order_id && (additionalData?.type === 'EVENT_DAILY_REMINDER' || 
                                             additionalData?.type === 'EVENT_AFTERNOON_REMINDER' || 
                                             additionalData?.type === 'EVENT_ATTENDANCE_CONFIRMATION')) {
        navigate(SCREENS.PAYMENT_SUCCESS, { order_id: additionalData.order_id, fromNotifications: true });
      } else if (additionalData?.event_id && additionalData?.type === 'EVENT_REQUESTING') {
        navigate(SCREENS.PENDING_ATTENDEES, { eventId: Number(additionalData.event_id), eventName: 'Event' });
      } else if (additionalData?.type === 'EVENT_DELETED') {
        return;
      } else if (additionalData?.refineSubcategories) {
        navigate(SCREENS.SETTINGS_STACK, { refineSubcategories: additionalData?.refineSubcategories });
      }else if (additionalData?.type === 'SUBSCRIPTION_REMINDER') {
        navigate(SCREENS.SUBSCRIPTIONS as never);
      }
    });
  }, [navigate, openChat, openEvent]);

  const styles = StyleSheet.create({
    tabBar: {
      backgroundColor: colors.white,
      paddingTop: 8,
      paddingBottom: Platform.OS === 'ios' ? 24 : 8,
      borderTopWidth: 1,
      borderTopColor: '#F2F2F2',
      elevation: 0,
      shadowOpacity: 0,
      height: Platform.OS === 'ios' ? 84 : 60,
    },
    tabBarLabel: {
      fontSize: 11,
      fontWeight: '500',
      marginTop: 4,
      marginBottom: Platform.OS === 'ios' ? 0 : 4,
    },
    tabBarItem: {
      justifyContent: 'center',
      alignItems: 'center',
      paddingTop: 6,
    },
    homeIcon: {
      transform: [{ scale: 1.2 }], // Make the home icon slightly larger
    },
    badgeContainer: {
      position: 'absolute',
      top: -5,
      right: -5,
      backgroundColor: colors.error,
      borderRadius: 10,
      width: 20,
      height: 20,
      justifyContent: 'center',
      alignItems: 'center',
    },
    badgeText: {
      color: colors.white,
      fontSize: 12,
      fontWeight: 'bold',
    },
    dotBadge: {
      width: 8,
      height: 8,
      borderRadius: 4,
      backgroundColor: colors.error,
      position: 'absolute',
      top: -2,
      right: -2,
    },
  });

  return (
    <Tabs.Navigator
      screenOptions={{
        unmountOnBlur: true,
        lazy: false,
        tabBarHideOnKeyboard: true,
        headerShown: false,
        tabBarStyle: isTabBarDisabled ? { display: 'none' } : styles.tabBar,
        tabBarActiveTintColor: colors.primary,
        tabBarInactiveTintColor: '#999999',
        tabBarShowLabel: true,
        tabBarLabelStyle: styles.tabBarLabel,
        tabBarIconStyle: { marginBottom: -2 },
      }}
      initialRouteName={SCREENS.HOME_STACK}>
      {appScreens.map(screen => (
        <Tabs.Screen
          component={screen.component}
          key={screen.name}
          name={screen.name}
          listeners={() => ({})}
          initialParams={screen.params}
          options={{
            tabBarLabel: screen.name === SCREENS.HOME_STACK ? 'Home' : t(screen.title),
            tabBarIcon: ({ focused, color }: { focused: boolean; color: string }) => {
              return (
                <View style={[styles.tabBarItem, screen.name === SCREENS.HOME_STACK ? styles.homeIcon : null]}>
                  {/* For Home tab, always use the same icon regardless of focus state */}
                  {screen.name === SCREENS.HOME_STACK ? 
                    <screen.icon color={color} /> : 
                    (focused ? <screen.iconBar color={color} /> : <screen.icon color={color} />)
                  }
                  {/* Add the Badge with Unread Count */}
                  {!!unreadChatCount && screen.name === 'SCREEN:CHAT_STACK' && (
                    <View style={styles.badgeContainer}>
                      <Text style={styles.badgeText}>{unreadChatCount + ''}</Text>
                    </View>
                  )}
                  {/* Add the Notifications Badge */}
                  {!!optimisticUnreadCount && optimisticUnreadCount > 0 && user && screen.name === SCREENS.NOTIFICATIONS && (
                    <View style={[styles.badgeContainer, optimisticUnreadCount === 1 ? styles.dotBadge : null]}>
                      {optimisticUnreadCount === 1 ? null : (
                        <Text style={styles.badgeText}>{optimisticUnreadCount > 99 ? '99+' : optimisticUnreadCount + ''}</Text>
                      )}
                    </View>
                  )}
                </View>
              );
            },
          }}
        />
      ))}
    </Tabs.Navigator>
  );
};

export default AppScreens;
